import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  DashboardSummaryDto,
  JournalActivityDto,
  LedgerAccountDto,
  LedgerEntryDto,
  LedgerJournalDto,
  BalanceContainerDto,
  ProviderAccountDto,
  ProviderVirtualAccountDto,
  CustomerTransactionDto,
  FilterMetadataDto,
  ReconciliationStatusDto,
  BalanceByClassDto,
  LedgerJournalDetailDto,
  BalanceContainerDetailDto,
  LedgerAccountDetailDto,
  ProviderAccountDetailDto,
  ProviderVirtualAccountDetailDto,
  CustomerTransactionDetailDto,
} from '@/types/ledger.types';
import { PaginationAPI } from '@/types/pagination';
import { Pagination } from '@/constants/pagination';

interface PaginatedState<T> {
  data: T[];
  pagination: PaginationAPI;
  loading: boolean;
}

const defaultPaginatedState = {
  data: [],
  pagination: { Page: Pagination.Page, totalPages: 0, totalElements: 0, Size: Pagination.Size },
  loading: false,
};

export interface LedgerState {
  dashboard: {
    summary: DashboardSummaryDto | null;
    activity: JournalActivityDto[];
    loading: boolean;
  };
  filterMetadata: FilterMetadataDto | null;
  accounts: PaginatedState<LedgerAccountDto> & { detail: LedgerAccountDetailDto | null };
  journals: PaginatedState<LedgerJournalDto> & { detail: LedgerJournalDetailDto | null };
  entries: PaginatedState<LedgerEntryDto>;
  balanceContainers: PaginatedState<BalanceContainerDto> & { detail: BalanceContainerDetailDto | null };
  providerAccounts: PaginatedState<ProviderAccountDto> & { detail: ProviderAccountDetailDto | null };
  virtualAccounts: PaginatedState<ProviderVirtualAccountDto> & { detail: ProviderVirtualAccountDetailDto | null };
  customerTransactions: PaginatedState<CustomerTransactionDto> & { detail: CustomerTransactionDetailDto | null };
  reconciliation: {
    status: ReconciliationStatusDto | null;
    balanceByClass: BalanceByClassDto[];
    loading: boolean;
  };
}

export const ledgerInitialState: LedgerState = {
  dashboard: { summary: null, activity: [], loading: false },
  filterMetadata: null,
  accounts: { ...defaultPaginatedState, detail: null },
  journals: { ...defaultPaginatedState, detail: null },
  entries: { ...defaultPaginatedState },
  balanceContainers: { ...defaultPaginatedState, detail: null },
  providerAccounts: { ...defaultPaginatedState, detail: null },
  virtualAccounts: { ...defaultPaginatedState, detail: null },
  customerTransactions: { ...defaultPaginatedState, detail: null },
  reconciliation: { status: null, balanceByClass: [], loading: false },
};

const slice = createSlice({
  name: 'ledger',
  initialState: ledgerInitialState,
  reducers: {
    setFilterMetadata(state, action: PayloadAction<FilterMetadataDto>) {
      state.filterMetadata = action.payload;
    },
    setDashboardSummary(state, action: PayloadAction<DashboardSummaryDto>) {
      state.dashboard.summary = action.payload;
    },
    setDashboardActivity(state, action: PayloadAction<JournalActivityDto[]>) {
      state.dashboard.activity = action.payload;
    },
    setDashboardLoading(state, action: PayloadAction<boolean>) {
      state.dashboard.loading = action.payload;
    },
    setLedgerAccounts(state, action: PayloadAction<{ data: LedgerAccountDto[]; pagination: PaginationAPI }>) {
      state.accounts.data = action.payload.data;
      state.accounts.pagination = action.payload.pagination;
    },
    setLedgerAccountsLoading(state, action: PayloadAction<boolean>) {
      state.accounts.loading = action.payload;
    },
    setJournals(state, action: PayloadAction<{ data: LedgerJournalDto[]; pagination: PaginationAPI }>) {
      state.journals.data = action.payload.data;
      state.journals.pagination = action.payload.pagination;
    },
    setJournalDetail(state, action: PayloadAction<LedgerJournalDetailDto>) {
      state.journals.detail = action.payload;
    },
    setJournalsLoading(state, action: PayloadAction<boolean>) {
      state.journals.loading = action.payload;
    },
    setEntries(state, action: PayloadAction<{ data: LedgerEntryDto[]; pagination: PaginationAPI }>) {
      state.entries.data = action.payload.data;
      state.entries.pagination = action.payload.pagination;
    },
    setEntriesLoading(state, action: PayloadAction<boolean>) {
      state.entries.loading = action.payload;
    },
    setBalanceContainers(state, action: PayloadAction<{ data: BalanceContainerDto[]; pagination: PaginationAPI }>) {
      state.balanceContainers.data = action.payload.data;
      state.balanceContainers.pagination = action.payload.pagination;
    },
    setBalanceContainerDetail(state, action: PayloadAction<BalanceContainerDetailDto>) {
      state.balanceContainers.detail = action.payload;
    },
    setBalanceContainersLoading(state, action: PayloadAction<boolean>) {
      state.balanceContainers.loading = action.payload;
    },
    setProviderAccounts(state, action: PayloadAction<{ data: ProviderAccountDto[]; pagination: PaginationAPI }>) {
      state.providerAccounts.data = action.payload.data;
      state.providerAccounts.pagination = action.payload.pagination;
    },
    setProviderAccountsLoading(state, action: PayloadAction<boolean>) {
      state.providerAccounts.loading = action.payload;
    },
    setVirtualAccounts(state, action: PayloadAction<{ data: ProviderVirtualAccountDto[]; pagination: PaginationAPI }>) {
      state.virtualAccounts.data = action.payload.data;
      state.virtualAccounts.pagination = action.payload.pagination;
    },
    setVirtualAccountsLoading(state, action: PayloadAction<boolean>) {
      state.virtualAccounts.loading = action.payload;
    },
    setCustomerTransactions(state, action: PayloadAction<{ data: CustomerTransactionDto[]; pagination: PaginationAPI }>) {
      state.customerTransactions.data = action.payload.data;
      state.customerTransactions.pagination = action.payload.pagination;
    },
    setCustomerTransactionsLoading(state, action: PayloadAction<boolean>) {
      state.customerTransactions.loading = action.payload;
    },
    setLedgerAccountDetail(state, action: PayloadAction<LedgerAccountDetailDto>) {
      state.accounts.detail = action.payload;
    },
    setProviderAccountDetail(state, action: PayloadAction<ProviderAccountDetailDto>) {
      state.providerAccounts.detail = action.payload;
    },
    setVirtualAccountDetail(state, action: PayloadAction<ProviderVirtualAccountDetailDto>) {
      state.virtualAccounts.detail = action.payload;
    },
    setCustomerTransactionDetail(state, action: PayloadAction<CustomerTransactionDetailDto>) {
      state.customerTransactions.detail = action.payload;
    },
    setReconciliationStatus(state, action: PayloadAction<ReconciliationStatusDto>) {
      state.reconciliation.status = action.payload;
    },
    setReconciliationBalanceByClass(state, action: PayloadAction<BalanceByClassDto[]>) {
      state.reconciliation.balanceByClass = action.payload;
    },
    setReconciliationLoading(state, action: PayloadAction<boolean>) {
      state.reconciliation.loading = action.payload;
    },
  },
});

export const ledgerActions = slice.actions;
export default slice.reducer;
